// import firebase from "firebase/compat/app"
import { useEffect, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { AutoSizer } from "react-virtualized-auto-sizer"
import { List } from "react-window"
import { IBotLight } from "../../../../../models/mongo-models/bot-v2"
import { Pkm } from "../../../../../types/enum/Pokemon"
import { authenticateUser } from "../../../network"
import { cc } from "../../utils/jsx"
import PokemonPortrait from "../pokemon-portrait"
import { PokemonTypeahead } from "../typeahead/pokemon-typeahead"
import "./bot-manager-panel.css"
import { gitee } from "../../utils/gitee-auth"
import { webBasePath } from "../../utils/fetchConfigClient"
import { BOT_TAGS, normalizeBotTags } from "../../../../../utils/bot-tags"
import { Modal } from "../modal/modal"
import { Checkbox } from "../checkbox/checkbox"

const ROW_HEIGHT = 50

export function BotManagerPanel() {
  const [filterApproved, setFilterApproved] = useState<boolean | undefined>()
  const [filteredPokemon, setFilteredPokemon] = useState<Pkm | "">("")
  const [filteredAuthor, setFilteredAuthor] = useState<string>("")
  const [filteredTag, setFilteredTag] = useState<string>("")
  const [authors, setAuthors] = useState<string[]>([])
  const [editingBot, setEditingBot] = useState<IBotLight | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const navigate = useNavigate()
  const { t } = useTranslation()
  return (
    <div id="bot-manager-panel">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <h1>Bot Management Panel</h1>
        <button onClick={() => navigate("/lobby")} className="bubbly blue">
          {t("back_to_lobby")}
        </button>
      </div>

      <div className="controls">
        <button
          className="bubbly blue"
          onClick={() => setFilterApproved(undefined)}
        >
          All Bots
        </button>
        <button
          className="bubbly green"
          onClick={() => setFilterApproved(true)}
        >
          Approved Bots
        </button>
        <button
          className="bubbly orange"
          onClick={() => setFilterApproved(false)}
        >
          Bots pending approval
        </button>
        <div className="spacer"></div>
        <div>
          Filter bots using this Pokémon:&nbsp;
          <PokemonTypeahead
            value={filteredPokemon}
            onChange={setFilteredPokemon}
          />
        </div>
        <div>
          Filter by author:&nbsp;
          <select
            value={filteredAuthor}
            onChange={(e) => setFilteredAuthor(e.target.value)}
            className="author-filter-input"
          >
            <option value="">All authors</option>
            {authors.map((author) => (
              <option key={author} value={author}>
                {author}
              </option>
            ))}
          </select>
        </div>
        <div>
          Filter by tag:&nbsp;
          <select
            value={filteredTag}
            onChange={(e) => setFilteredTag(e.target.value)}
            className="author-filter-input"
          >
            <option value="">All tags</option>
            {BOT_TAGS.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
        </div>
      </div>
      <BotsList
        approved={filterApproved}
        filteredPokemon={filteredPokemon}
        filteredAuthor={filteredAuthor}
        filteredTag={filteredTag}
        reloadKey={reloadKey}
        onBotsLoaded={(bots) =>
          setAuthors([...new Set(bots.map((b) => b.author))].sort())
        }
        onEditBot={(bot) => setEditingBot(bot)}
      />
      {editingBot && (
        <BotTagsModal
          bot={editingBot}
          close={() => setEditingBot(null)}
          onSaved={() => {
            setEditingBot(null)
            setReloadKey((key) => key + 1)
          }}
        />
      )}
    </div>
  )
}

function BotsList(props: {
  approved?: boolean
  filteredPokemon: Pkm | ""
  filteredAuthor: string
  filteredTag: string
  reloadKey: number
  onBotsLoaded: (bots: IBotLight[]) => void
  onEditBot: (bot: IBotLight) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [bots, setBots] = useState<IBotLight[] | null>(null)
  const [sortColumn, setSortColumn] = useState<string>("")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")

  useEffect(() => {
    authenticateUser()
    // fetch(webBasePath+`/bots?t=${Date.now()}`)
    fetch(
      webBasePath+`/bots?${props.filteredPokemon ? `pkm=${props.filteredPokemon}&` : ""}${props.filteredTag ? `tag=${props.filteredTag}&` : ""}t=${Date.now()}`
    )
      .then((res) => res.json())
      .then((data) => {
        setBots(data)
        props.onBotsLoaded(data)
      })
  }, [props.filteredPokemon, props.filteredTag, props.reloadKey])

  async function deleteBot(bot: IBotLight) {
    if (!confirm(`Are you sure you want to delete bot ${bot.name} of ${bot.author} ?`)) return
    // const token = await firebase.auth().currentUser?.getIdToken()
    const token = await gitee.getAuth().then(user => user.idToken);


    const res = await fetch(webBasePath+`/bots/${bot.id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    if (res.ok) {
      setBots((bots) => bots?.filter((b) => b.id !== bot.id) ?? [])
    } else alert(res.statusText)
  }

  async function approveBot(botId: string, approved: boolean) {
    const token = await gitee.getAuth().then(user => user.idToken);
    // const token = await firebase.auth().currentUser?.getIdToken()
    const res = await fetch(webBasePath+`/bots/${botId}/approve`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ approved })
    })
    if (res.ok) {
      setBots(
        (bots) =>
          bots?.map((bot) => (bot.id === botId ? { ...bot, approved } : bot)) ??
          []
      )
    } else alert(res.statusText)
  }
  async function lockBotElo(botId: string, isLockElo: boolean) {
    const token = await gitee.getAuth().then(user => user.idToken);
    // const token = await firebase.auth().currentUser?.getIdToken()
    const res = await fetch(webBasePath+`/bots/${botId}/lockElo`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ isLockElo })
    })
    if (res.ok) {
      setBots(
        (bots) =>
          bots?.map((bot) => (bot.id === botId ? { ...bot, isLockElo } : bot)) ??
          []
      )
    } else alert(res.statusText)
  } //  lockElo

  function handleSort(column: string) {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortColumn(column)
      setSortDirection("asc")
    }
  }

  const filteredBots = useMemo(() => {
    if (!bots) return []
    return bots
      .filter(
        (b) => props.approved === undefined || b.approved === props.approved
      )
      .filter((b) => !props.filteredAuthor || b.author === props.filteredAuthor)
      .filter(
        (b) => !props.filteredTag || normalizeBotTags(b.tags).includes(props.filteredTag as never)
      )
      .sort((a, b) => {
        if (!sortColumn) return 0
        let aValue = a[sortColumn as keyof IBotLight]
        let bValue = b[sortColumn as keyof IBotLight]
        // Special handling for name (translated), elo (number), and avatar (string)
        if (sortColumn === "name") {
          aValue = t(`pkm.${a.name}`)
          bValue = t(`pkm.${b.name}`)
        }
        if (sortColumn === "elo") {
          aValue = Number(a.elo)
          bValue = Number(b.elo)
        }
        if (aValue === undefined || bValue === undefined) return 0
        if (typeof aValue === "string" && typeof bValue === "string") {
          const cmp = aValue.localeCompare(bValue)
          return sortDirection === "asc" ? cmp : -cmp
        }
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
        return 0
      })
  }, [bots, props.approved, sortColumn, sortDirection])

  return (
    <main id="bots-list" className="my-container">
      {bots === null ? (
        <p>Loading...</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th
                onClick={() => handleSort("avatar")}
                style={{ cursor: "pointer" }}
              >
                Avatar{" "}
                {sortColumn === "avatar"
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              <th
                onClick={() => handleSort("name")}
                style={{ cursor: "pointer" }}
              >
                Name{" "}
                {sortColumn === "name"
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              <th
                onClick={() => handleSort("author")}
                style={{ cursor: "pointer" }}
              >
                Author{" "}
                {sortColumn === "author"
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              <th
                onClick={() => handleSort("elo")}
                style={{ cursor: "pointer" }}
              >
                Elo{" "}
                {sortColumn === "elo"
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              <th
                onClick={() => handleSort("updatedAt")}
                style={{ cursor: "pointer" }}
              >
                updatedAt{" "}
                {sortColumn === "updatedAt"
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              <th>Tags</th>
              <th
                onClick={() => handleSort("id")}
                style={{ cursor: "pointer" }}
              >
                UID{" "}
                {sortColumn === "id"
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              <th
                onClick={() => handleSort("isLockElo")}
                style={{ cursor: "pointer" }}
              >
                LockElo{" "}
                {sortColumn === "isLockElo"
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              <th
                onClick={() => handleSort("approved")}
                style={{ cursor: "pointer" }}
              >
                Approved{" "}
                {sortColumn === "approved"
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBots.map((b) => (
              <tr key={b.id}>
                <td>
                  <PokemonPortrait avatar={b.avatar} />
                </td>
                <td>{t(`pkm.${b.name}`)}</td>
                <td>{b.author}</td>
                <td>{b.elo}</td>
                <td>{b.updatedAt ? new Date(b.updatedAt).toLocaleString() : ""}</td>
                <td style={{ color: "#999", fontSize: "80%" }}>{b.id}</td>
                <td>
                  {b.isLockElo ? (
                    <span style={{ color: "lime" }}>{t("yes")}</span>
                  ) : (
                    <span style={{ color: "red" }}>{t("no")}</span>
                  )}
                </td>
                <td>
                  {b.approved ? (
                    <span style={{ color: "lime" }}>{t("yes")}</span>
                  ) : (
                    <span style={{ color: "red" }}>{t("no")}</span>
                  )}
                </td>
                <td>
                  <div className="bot-tags-list">
                    {normalizeBotTags(b.tags).map((tag) => (
                      <span key={tag} className="bot-tag-pill">
                        {tag}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <button
                    onClick={() => navigate(`/bot-builder?bot=${b.id}`)}
                    className="bubbly blue"
                    style={{ fontSize: "80%" }}
                  >
                    {t("edit")}
                  </button>
                  <button
                    onClick={() => props.onEditBot(b)}
                    className="bubbly blue"
                    style={{ fontSize: "80%" }}
                  >
                    Edit Tags
                  </button>
                  <button
                    onClick={() => lockBotElo(b.id, !b.isLockElo)}
                    className={cc("bubbly", !b.isLockElo ? "orange" : "green")}
                    style={{ fontSize: "80%" }}
                  >
                    { !b.isLockElo ? t("LockElo") : t("noLockElo")}
                  </button>
                  <button
                    onClick={() => approveBot(b.id, !b.approved)}
                    className={cc("bubbly", b.approved ? "orange" : "green")}
                    style={{ fontSize: "80%" }}
                  >
                    {b.approved ? t("disapprove") : t("approve")}
                  </button>
                  <button
                    onClick={() => deleteBot(b)}
                    className="bubbly red"
                    style={{ fontSize: "80%" }}
                  >
                    {t("delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  )
}

function BotTagsModal(props: {
  bot: IBotLight
  close: () => void
  onSaved: () => void
}) {
  const [tags, setTags] = useState<string[]>(normalizeBotTags(props.bot.tags))
  const [saving, setSaving] = useState(false)
  const { t } = useTranslation()

  async function saveTags() {
    if (saving) return
    setSaving(true)
    const token = await gitee.getAuth().then((user) => user.idToken)
    const res = await fetch(webBasePath + `/bots/${props.bot.id}/tags`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ tags: normalizeBotTags(tags) })
    })
    setSaving(false)
    if (res.ok) {
      props.onSaved()
    } else {
      alert(await res.text())
    }
  }

  return (
    <Modal
      show={true}
      onClose={props.close}
      header={`Edit tags for ${props.bot.name}`}
      body={
        <div className="bot-tags-editor">
          {BOT_TAGS.map((tag) => (
            // <label key={tag} className="bot-tag-checkbox">
            //   <input
            //     type="checkbox"
            //     className="my-checkbox is-dark"
            //     checked={tags.includes(tag)}
            //     onChange={() =>
            //       setTags((current) =>
            //         current.includes(tag)
            //           ? current.filter((value) => value !== tag)
            //           : [...current, tag]
            //       )
            //     }
            //   />
            //   {tag}
            // </label>
            <Checkbox
                        key={tag}
                        checked={tags.includes(tag)}
                        disabled={false}
                        onToggle={(checked) => {
                          setTags((current) =>
                            current.includes(tag)
                              ? current.filter((value) => value !== tag)
                              : [...current, tag]
                          )
                        }}
                        label={tag}
                        isDark
                      />
          ))}
        </div>
      }
      footer={
        <>
          <button className="bubbly red" onClick={props.close}>
            {t("cancel")}
          </button>
          <button className="bubbly green" onClick={saveTags}>
            {saving ? "Saving..." : t("save")}
          </button>
        </>
      }
    />
  )
}

